import { trpc } from '../../trpc-client.js';

export type ConfigRepoSkillStatus =
  | 'new'
  | 'in_sync'
  | 'changed'
  | 'unpublished'
  | 'deleted'
  | 'error';

export interface ConfigRepoSkillRow {
  readonly skill_name: string;
  readonly description: string | null;
  readonly repo_tree_hash: string | null;
  readonly published_tree_hash: string | null;
  readonly ma_skill_id: string | null;
  readonly status: ConfigRepoSkillStatus;
  readonly repo_path: string | null;
  readonly scanned_at: string | null;
  readonly published_at: string | null;
  readonly updated_at: string | null;
  readonly scan_error: string | null;
}

export interface SkillActionButtonProps {
  readonly status: ConfigRepoSkillStatus;
  readonly isPending: boolean;
  readonly onSync: () => void;
  readonly onDelete: () => void;
}

export type SkillAction =
  | { readonly kind: 'sync'; readonly label: 'Sync' }
  | { readonly kind: 'delete'; readonly label: 'Delete' };

export function formatSkillStatus(status: ConfigRepoSkillStatus): string {
  switch (status) {
    case 'new':
      return 'New';
    case 'in_sync':
      return 'In sync';
    case 'changed':
      return 'Changed';
    case 'unpublished':
      return 'Unpublished';
    case 'deleted':
      return 'Deleted';
    case 'error':
      return 'Error';
  }
}

export function canSyncSkill(status: ConfigRepoSkillStatus): boolean {
  return status === 'new' || status === 'changed' || status === 'unpublished';
}

export function getSkillAction(status: ConfigRepoSkillStatus): SkillAction | null {
  if (canSyncSkill(status)) {
    return { kind: 'sync', label: 'Sync' };
  }
  if (status === 'deleted') {
    return { kind: 'delete', label: 'Delete' };
  }
  return null;
}

export function SkillActionButton(props: SkillActionButtonProps): React.ReactElement | null {
  const action = getSkillAction(props.status);
  if (action === null) return null;
  const isDelete = action.kind === 'delete';
  const pendingLabel = isDelete ? 'Deleting...' : 'Syncing...';
  return (
    <button
      type="button"
      className={isDelete ? 'skills-sync-button skills-delete-button' : 'skills-sync-button'}
      disabled={props.isPending}
      onClick={isDelete ? props.onDelete : props.onSync}
    >
      {props.isPending ? pendingLabel : action.label}
    </button>
  );
}

function formatDate(value: string | null): string {
  if (value === null) return '-';
  return new Date(value).toLocaleString();
}

function shortHash(value: string | null): string {
  if (value === null || value.length === 0) return '-';
  return value.length <= 12 ? value : value.slice(0, 12);
}

export function SkillsListPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const skillsQuery = trpc.skills.list.useQuery({});
  const scanConfigRepo = trpc.skills.scanConfigRepo.useMutation();
  const syncSkill = trpc.skills.sync.useMutation();
  const deleteSkill = trpc.skills.delete.useMutation();

  const skills = (skillsQuery.data?.data ?? []) as readonly ConfigRepoSkillRow[];
  const lastScanAt = skillsQuery.data?.last_scan_at ?? null;

  if (skillsQuery.isPending) {
    return (
      <section className="page page-skills" aria-busy="true">
        <h1>Skills</h1>
        <p>Loading skills...</p>
      </section>
    );
  }

  if (skillsQuery.error !== null) {
    return (
      <section className="page page-skills" role="alert">
        <h1>Skills</h1>
        <p>Failed to load skills: {skillsQuery.error.message}</p>
      </section>
    );
  }

  return (
    <section className="page page-skills">
      <div className="skills-header">
        <div>
          <h1>Skills</h1>
          <p className="skills-header-meta">Last scanned: {formatDate(lastScanAt)}</p>
        </div>
        <button
          type="button"
          className="skills-scan-button"
          disabled={scanConfigRepo.isPending}
          onClick={() => {
            void scanConfigRepo.mutateAsync(undefined).then(async () => {
              await utils.skills.list.invalidate();
            });
          }}
        >
          {scanConfigRepo.isPending ? 'Scanning...' : 'Scan config repo'}
        </button>
      </div>

      {skills.length === 0 ? (
        <p className="skills-empty">No skills scanned yet.</p>
      ) : (
        <ul className="skills-list" aria-label="Skills">
          {skills.map((skill) => (
            <li key={skill.skill_name} className="skill-card">
              <div className="skill-card-header">
                <span className="skill-card-name">{skill.skill_name}</span>
                <div className="skill-card-actions">
                  <span className={`sb-chip skills-status-${skill.status}`}>
                    {formatSkillStatus(skill.status)}
                  </span>
                  <SkillActionButton
                    status={skill.status}
                    isPending={
                      (syncSkill.isPending &&
                        syncSkill.variables?.skillName === skill.skill_name) ||
                      (deleteSkill.isPending &&
                        deleteSkill.variables?.skillName === skill.skill_name)
                    }
                    onSync={() => {
                      void syncSkill.mutateAsync({ skillName: skill.skill_name }).then(async () => {
                        await utils.skills.list.invalidate();
                      });
                    }}
                    onDelete={() => {
                      void deleteSkill
                        .mutateAsync({ skillName: skill.skill_name })
                        .then(async () => {
                          await utils.skills.list.invalidate();
                        });
                    }}
                  />
                </div>
              </div>

              <div
                className={
                  skill.description === null
                    ? 'skill-card-description is-placeholder'
                    : 'skill-card-description'
                }
              >
                {skill.description ?? 'No description'}
                {skill.scan_error !== null && (
                  <span className="skills-row-error">{skill.scan_error}</span>
                )}
              </div>

              <div className="skill-card-meta">
                <span className="skill-meta-item">
                  <span className="skill-meta-label">MA ID</span>
                  <span
                    className={
                      skill.ma_skill_id !== null ? 'skill-meta-value' : 'skill-meta-value is-plain'
                    }
                  >
                    {skill.ma_skill_id ?? 'Not synced'}
                  </span>
                </span>
                <span className="skill-meta-item">
                  <span className="skill-meta-label">Repo</span>
                  <span className="skill-meta-value">{shortHash(skill.repo_tree_hash)}</span>
                </span>
                <span className="skill-meta-item">
                  <span className="skill-meta-label">Published</span>
                  <span className="skill-meta-value">{shortHash(skill.published_tree_hash)}</span>
                </span>
                <span className="skill-meta-item">
                  <span className="skill-meta-label">Scanned</span>
                  <span className="skill-meta-value is-plain">{formatDate(skill.scanned_at)}</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {scanConfigRepo.data !== undefined && (
        <p className="skills-result">
          Scan found {scanConfigRepo.data.discovered_count} skills;{' '}
          {scanConfigRepo.data.valid_count} valid, {scanConfigRepo.data.invalid_count} invalid.
        </p>
      )}
      {scanConfigRepo.error !== null && (
        <p role="alert" className="skills-error">
          Failed to scan config repo: {scanConfigRepo.error.message}
        </p>
      )}
      {syncSkill.error !== null && (
        <p role="alert" className="skills-error">
          Failed to sync skill: {syncSkill.error.message}
        </p>
      )}
      {deleteSkill.error !== null && (
        <p role="alert" className="skills-error">
          Failed to delete skill: {deleteSkill.error.message}
        </p>
      )}
    </section>
  );
}
